import {
    Box,
    Heading,
    Text,
    Card,
    CardHeader,
    CardBody,
    VStack,
    FormControl,
    FormLabel,
    Input,
    Button,
    Image,
    useToast,
    Flex,
    Icon,
    Divider,
    Progress,
    Grid,
    Tabs,
    TabList,
    TabPanels,
    Tab,
    TabPanel,
} from '@chakra-ui/react';
import { useUser } from '@clerk/clerk-react';
import { useState, useRef, useEffect } from 'react';
import { MdCloudUpload, MdBrandingWatermark, MdPerson, MdBusiness, MdPeople, MdWorkHistory, MdVerified } from 'react-icons/md';
import axios from 'axios';
import { CompanyInfoTab } from '../components/Settings/CompanyInfoTab';
import { KeyPersonnelTab } from '../components/Settings/KeyPersonnelTab';
import { PastPerformanceTab } from '../components/Settings/PastPerformanceTab';
import { CertificationsTab } from '../components/Settings/CertificationsTab';

const API_URL = (import.meta as any).env.VITE_API_URL || 'http://localhost:8000';

export function Settings() {
    const { user, isLoaded } = useUser();
    const toast = useToast();
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [uploading, setUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [removing, setRemoving] = useState(false);

    // Account states
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [savingAccount, setSavingAccount] = useState(false);

    // Sync state with user data once loaded
    useEffect(() => {
        if (isLoaded && user) {
            setFirstName(user.firstName || '');
            setLastName(user.lastName || '');
        }
    }, [isLoaded, user]);

    const handleUpdateAccount = async () => {
        if (!user) return;

        setSavingAccount(true);
        try {
            await user.update({
                firstName,
                lastName
            });
            toast({
                title: 'Account Updated',
                description: 'Your profile has been updated successfully.',
                status: 'success',
                duration: 3000,
                isClosable: true,
            });
        } catch (error) {
            console.error('Update failed:', error);
            toast({
                title: 'Update Failed',
                description: 'Could not update your account. Please try again.',
                status: 'error',
                duration: 3000,
            });
        } finally {
            setSavingAccount(false);
        }
    };

    const handleUploadClick = () => {
        fileInputRef.current?.click();
    };

    const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) return;

        // Validation
        if (!file.type.startsWith('image/')) {
            toast({
                title: 'Invalid file type',
                description: 'Please upload an image file (PNG, JPG, SVG).',
                status: 'error',
                duration: 3000,
            });
            return;
        }

        if (file.size > 5 * 1024 * 1024) { // 5MB limit
            toast({
                title: 'File too large',
                description: 'Please upload an image smaller than 5MB.',
                status: 'error',
                duration: 3000,
            });
            return;
        }

        setUploading(true);
        setUploadProgress(0);

        try {
            // 1. Upload to Backend
            const formData = new FormData();
            formData.append('file', file);

            // Get token via global window object cast (or useClerk hook in cleaner way, but this works)
            const token = await (window as any).Clerk?.session?.getToken();

            await axios.post(`${API_URL}/v1/users/me/logo`, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                    'Authorization': `Bearer ${token}`
                },
                onUploadProgress: (progressEvent) => {
                    const percentCompleted = Math.round((progressEvent.loaded * 100) / (progressEvent.total || file.size));
                    setUploadProgress(percentCompleted);
                }
            });

            // 2. Reload user to get updated metadata from Clerk (since Backend updated it)
            await user?.reload();

            toast({
                title: 'Logo Updated',
                description: 'Your organization logo has been updated successfully.',
                status: 'success',
                duration: 3000,
                isClosable: true,
            });

        } catch (error) {
            console.error('Upload failed:', error);

            // Check if it's the backend failing to update Clerk
            if (axios.isAxiosError(error) && error.response?.status === 500) {
                toast({
                    title: 'Upload Issue',
                    description: 'Image uploaded but settings could not be saved. Check backend logs.',
                    status: 'warning',
                    duration: 5000,
                });
            } else {
                toast({
                    title: 'Upload Failed',
                    description: 'Could not upload logo. Please try again.',
                    status: 'error',
                    duration: 3000,
                });
            }
        } finally {
            setUploading(false);
            setUploadProgress(0);
            // Reset input
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        }
    };

    const handleRemoveLogo = async () => {
        if (!user) return;
        setRemoving(true);
        try {
            const token = await (window as any).Clerk?.session?.getToken();
            await axios.delete(`${API_URL}/v1/users/me/logo`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });
            await user.reload();
            toast({
                title: 'Logo Removed',
                description: 'Your organization logo has been removed.',
                status: 'success',
                duration: 3000,
                isClosable: true,
            });
        } catch (error) {
            console.error('Remove failed:', error);
            toast({
                title: 'Remove Failed',
                description: 'Could not remove logo. Please try again.',
                status: 'error',
                duration: 3000,
            });
        } finally {
            setRemoving(false);
        }
    };

    if (!isLoaded) return null;

    const currentLogo = (user?.publicMetadata?.logoUrl as string) || null;

    return (
        <Box maxW="container.xl" mx="auto">
            <Heading size="lg" mb={6}>Settings</Heading>

            <Tabs colorScheme="brand" variant="enclosed">
                <TabList mb="1em">
                    <Tab><Icon as={MdPerson} mr={2} /> My Account</Tab>
                    <Tab><Icon as={MdBusiness} mr={2} /> Company Profile</Tab>
                    <Tab><Icon as={MdPeople} mr={2} /> Key Personnel</Tab>
                    <Tab><Icon as={MdWorkHistory} mr={2} /> Past Performance</Tab>
                    <Tab><Icon as={MdVerified} mr={2} /> Certifications</Tab>
                </TabList>
                <TabPanels>
                    {/* Account Settings Tab */}
                    <TabPanel px={0}>
                        <VStack spacing={6} align="stretch" maxW="container.lg">
                            {/* Branding Card */}
                            <Card>
                                <CardHeader>
                                    <Flex align="center" gap={3}>
                                        <Icon as={MdBrandingWatermark} boxSize={6} color="brand.500" />
                                        <Box>
                                            <Heading size="md">Organization Branding</Heading>
                                            <Text fontSize="sm" color="gray.500">
                                                Customize how BidBandit looks for your team.
                                            </Text>
                                        </Box>
                                    </Flex>
                                </CardHeader>
                                <Divider color="gray.200" />
                                <CardBody>
                                    <VStack spacing={6} align="start">
                                        <FormControl>
                                            <FormLabel fontWeight="medium">Organization Logo</FormLabel>
                                            <Text fontSize="sm" color="gray.500" mb={4}>
                                                This logo will appear in the sidebar and on generated reports.
                                                Recommended size: 200x200px (PNG or SVG with transparent background).
                                            </Text>

                                            <Flex align="center" gap={6} direction={{ base: 'column', sm: 'row' }}>
                                                {/* Preview Box */}
                                                <Box
                                                    w="120px"
                                                    h="120px"
                                                    border="2px dashed"
                                                    borderColor="gray.300"
                                                    borderRadius="xl"
                                                    display="flex"
                                                    alignItems="center"
                                                    justifyContent="center"
                                                    bg="gray.50"
                                                    overflow="hidden"
                                                    position="relative"
                                                >
                                                    {currentLogo ? (
                                                        <Image
                                                            src={currentLogo}
                                                            alt="Organization Logo"
                                                            objectFit="contain"
                                                            w="100%"
                                                            h="100%"
                                                            p={2}
                                                        />
                                                    ) : (
                                                        <Text fontSize="xs" color="gray.400" textAlign="center" px={2}>
                                                            No Logo Uploaded
                                                        </Text>
                                                    )}
                                                </Box>

                                                {/* Actions */}
                                                <VStack align="start" spacing={3}>
                                                    <input
                                                        type="file"
                                                        ref={fileInputRef}
                                                        onChange={handleFileChange}
                                                        accept="image/*"
                                                        style={{ display: 'none' }}
                                                    />
                                                    <Button
                                                        leftIcon={<MdCloudUpload />}
                                                        colorScheme="brand"
                                                        onClick={handleUploadClick}
                                                        isLoading={uploading}
                                                        loadingText="Uploading..."
                                                        size="sm"
                                                    >
                                                        Upload New Logo
                                                    </Button>
                                                    {currentLogo && (
                                                        <Button
                                                            variant="outline"
                                                            colorScheme="red"
                                                            onClick={handleRemoveLogo}
                                                            isLoading={removing}
                                                            loadingText="Removing..."
                                                            size="sm"
                                                        >
                                                            Remove Logo
                                                        </Button>
                                                    )}
                                                    <Text fontSize="xs" color="gray.500">
                                                        Max file size: 5MB. Supported formats: PNG, JPG, SVG.
                                                    </Text>
                                                </VStack>
                                            </Flex>

                                            {uploading && (
                                                <Box mt={4}>
                                                    <Text fontSize="xs" mb={1} fontWeight="medium">Uploading: {uploadProgress}%</Text>
                                                    <Progress value={uploadProgress} size="xs" colorScheme="brand" borderRadius="full" />
                                                </Box>
                                            )}
                                        </FormControl>
                                    </VStack>
                                </CardBody>
                            </Card>

                            {/* Account Details Card */}
                            <Card>
                                <CardHeader>
                                    <Flex align="center" justify="space-between">
                                        <Heading size="md">Account Details</Heading>
                                        <Button
                                            size="sm"
                                            colorScheme="brand"
                                            onClick={handleUpdateAccount}
                                            isLoading={savingAccount}
                                        >
                                            Save Changes
                                        </Button>
                                    </Flex>
                                </CardHeader>
                                <Divider color="gray.200" />
                                <CardBody>
                                    <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)' }} gap={6}>
                                        <FormControl>
                                            <FormLabel fontWeight="medium">First Name</FormLabel>
                                            <Input
                                                value={firstName}
                                                onChange={(e) => setFirstName(e.target.value)}
                                                placeholder="Enter your first name"
                                            />
                                        </FormControl>
                                        <FormControl>
                                            <FormLabel fontWeight="medium">Last Name</FormLabel>
                                            <Input
                                                value={lastName}
                                                onChange={(e) => setLastName(e.target.value)}
                                                placeholder="Enter your last name"
                                            />
                                        </FormControl>
                                        <FormControl>
                                            <FormLabel fontWeight="medium">Email Address</FormLabel>
                                            <Input
                                                value={user?.primaryEmailAddress?.emailAddress || ''}
                                                isReadOnly
                                                bg="gray.50"
                                                cursor="not-allowed"
                                            />
                                            <Text fontSize="xs" color="gray.500" mt={1}>
                                                Email cannot be changed here.
                                            </Text>
                                        </FormControl>
                                    </Grid>
                                </CardBody>
                            </Card>
                        </VStack>
                    </TabPanel>

                    {/* Company Profile Tab */}
                    <TabPanel px={0}>
                        <CompanyInfoTab />
                    </TabPanel>

                    {/* Key Personnel Tab */}
                    <TabPanel px={0}>
                        <KeyPersonnelTab />
                    </TabPanel>

                    {/* Past Performance Tab */}
                    <TabPanel px={0}>
                        <PastPerformanceTab />
                    </TabPanel>

                    {/* Certifications Tab */}
                    <TabPanel px={0}>
                        <CertificationsTab />
                    </TabPanel>
                </TabPanels>
            </Tabs>
        </Box>
    );
}
